/**
 * OpportunityFormSteps
 * Step content components for the OpportunityForm multi-step wizard.
 * Each step renders form fields for a specific section.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Badge, Card } from 'react-bootstrap';

import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';
import useCapability from '@/shared/hooks/useCapability';
import { mergeTimezoneOption } from '@/shared/constants/timezones';

import {
    COMMITMENT_TYPES,
    APPLICATION_METHODS,
    FUNDING_TYPE_OPTIONS,
    AWARD_CYCLES,
    RECURRENCE_PATTERNS,
    buildOpportunityTypeCategories,
    isEventType,
    isFundingType,
} from '../utils/opportunityConstants';

/**
 * Step 1: Basics (title, type, description, focusAreas, tags)
 */
export function StepBasics({
    formData,
    handleChange,
    validationErrors,
    opportunityTypes,
}) {
    // Filtered against the tenant's own taxonomy.opportunityTypes; always
    // keeps formData.type present even if it's since fallen outside the
    // tenant's allowed list, so editing an existing opportunity never
    // shows a select with no matching option.
    const typeCategories = buildOpportunityTypeCategories(
        opportunityTypes,
        formData.type
    );

    return (
        <>
            <h4 className="text-primary mb-4">Basic Information</h4>

            <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.title}
                    placeholder="e.g., Summer STEM Internship Program"
                    maxLength={200}
                />
                <Form.Control.Feedback type="invalid">
                    {validationErrors.title}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                    {formData.title.length}/200 characters
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Opportunity Type *</Form.Label>
                <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.type}
                >
                    <option value="">Select a type</option>
                    {Object.entries(typeCategories).map(([category, types]) => (
                        <optgroup key={category} label={category}>
                            {types.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                    {validationErrors.type}
                </Form.Control.Feedback>
                {formData.type && (
                    <Form.Text className="text-muted">
                        {isEventType(formData.type) &&
                            'Event details step will be shown. '}
                        {isFundingType(formData.type) &&
                            'Funding details step will be shown.'}
                    </Form.Text>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.description}
                    placeholder="Describe this opportunity in detail..."
                    maxLength={5000}
                />
                <Form.Control.Feedback type="invalid">
                    {validationErrors.description}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                    {formData.description.length}/5000 characters
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Focus Areas</Form.Label>
                <Form.Control
                    type="text"
                    name="focusAreas"
                    value={formData.focusAreas}
                    onChange={handleChange}
                    placeholder="e.g., Computer Science, Biology, Data Analysis (comma-separated)"
                />
                <Form.Text className="text-muted">
                    Comma-separated list of focus areas
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Tags</Form.Label>
                <Form.Control
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    placeholder="e.g., beginner-friendly, paid, remote (comma-separated)"
                />
                <Form.Text className="text-muted">
                    Comma-separated tags to help people find this opportunity
                </Form.Text>
            </Form.Group>
        </>
    );
}

StepBasics.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    opportunityTypes: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Step 2: Audience & Location
 */
export function StepAudienceLocation({
    formData,
    handleChange,
    handleMultiSelect,
    targetAudiences,
    regions,
}) {
    const { regionsServedLabel } = useEcosystemTaxonomy();
    const showAddressFields =
        formData.locationMode === 'in-person' ||
        formData.locationMode === 'hybrid';

    // Template the region helper text on the tenant's authored label (e.g.
    // "Colorado regions" / "Texas regions"). When unset we drop the
    // region-specific qualifier rather than guessing: "Which regions does
    // this opportunity serve?" reads sensibly without a tenant qualifier.
    const regionsServedHelp = regionsServedLabel
        ? `Which ${regionsServedLabel} does this opportunity serve?`
        : 'Which regions does this opportunity serve?';

    return (
        <>
            <h4 className="text-primary mb-4">Audience & Location</h4>

            <Form.Group className="mb-3">
                <Form.Label>Target Audience</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                    Who is this opportunity for?
                </Form.Text>
                <div className="d-flex flex-wrap gap-2">
                    {targetAudiences.map((audience) => (
                        <Form.Check
                            key={audience}
                            type="checkbox"
                            id={`audience-${audience}`}
                            label={audience}
                            checked={(formData.targetAudience || []).includes(
                                audience
                            )}
                            onChange={() =>
                                handleMultiSelect('targetAudience', audience)
                            }
                        />
                    ))}
                </div>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Regions</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                    {regionsServedHelp}
                </Form.Text>
                <div className="d-flex flex-wrap gap-2">
                    {regions.map((region) => (
                        <Form.Check
                            key={region}
                            type="checkbox"
                            id={`opp-region-${region}`}
                            label={region}
                            checked={(formData.regions || []).includes(region)}
                            onChange={() =>
                                handleMultiSelect('regions', region)
                            }
                        />
                    ))}
                </div>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Location Mode</Form.Label>
                <div className="d-flex gap-3">
                    {['remote', 'in-person', 'hybrid'].map((mode) => (
                        <Form.Check
                            key={mode}
                            type="radio"
                            id={`location-${mode}`}
                            name="locationMode"
                            label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                            value={mode}
                            checked={formData.locationMode === mode}
                            onChange={handleChange}
                        />
                    ))}
                </div>
            </Form.Group>

            {showAddressFields && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Venue Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="venueName"
                            value={formData.venueName}
                            onChange={handleChange}
                            placeholder="e.g., Denver Convention Center"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Street address"
                        />
                    </Form.Group>

                    <Row>
                        <Col md={5}>
                            <Form.Group className="mb-3">
                                <Form.Label>City</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>State</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>ZIP Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    placeholder="ZIP"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

StepAudienceLocation.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleMultiSelect: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    targetAudiences: PropTypes.array.isRequired,
    regions: PropTypes.array.isRequired,
};

/**
 * Step 3: Schedule & Commitment
 */
export function StepScheduleCommitment({
    formData,
    handleChange,
    validationErrors,
}) {
    return (
        <>
            <h4 className="text-primary mb-4">Schedule & Commitment</h4>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.startDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.startDate}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    {!formData.isOngoing && (
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                                isInvalid={!!validationErrors.endDate}
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.endDate}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    id="isOngoing"
                    name="isOngoing"
                    label="This is an ongoing opportunity (no end date)"
                    checked={formData.isOngoing}
                    onChange={(e) =>
                        handleChange({
                            target: {
                                name: 'isOngoing',
                                value: e.target.checked,
                            },
                        })
                    }
                />
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Commitment Type</Form.Label>
                        <Form.Select
                            name="commitmentType"
                            value={formData.commitmentType}
                            onChange={handleChange}
                        >
                            <option value="">Select type</option>
                            {COMMITMENT_TYPES.map((ct) => (
                                <option key={ct.value} value={ct.value}>
                                    {ct.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    {['recurring', 'part-time'].includes(
                        formData.commitmentType
                    ) && (
                        <Form.Group className="mb-3">
                            <Form.Label>Hours per Week</Form.Label>
                            <Form.Control
                                type="number"
                                name="hoursPerWeek"
                                value={formData.hoursPerWeek}
                                onChange={handleChange}
                                min={0}
                                max={60}
                                isInvalid={!!validationErrors.hoursPerWeek}
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.hoursPerWeek}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Duration</Form.Label>
                <Form.Control
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="e.g., 8 weeks, 1 semester, 6 months"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    id="isPaid"
                    name="isPaid"
                    label="This is a paid opportunity"
                    checked={formData.isPaid}
                    onChange={(e) =>
                        handleChange({
                            target: {
                                name: 'isPaid',
                                value: e.target.checked,
                            },
                        })
                    }
                />
            </Form.Group>

            {formData.isPaid && (
                <Form.Group className="mb-3">
                    <Form.Label>Compensation Details</Form.Label>
                    <Form.Control
                        type="text"
                        name="compensationDetails"
                        value={formData.compensationDetails}
                        onChange={handleChange}
                        placeholder="e.g., $15/hour, $2000 stipend"
                    />
                </Form.Group>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Capacity</Form.Label>
                <Form.Control
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    min={0}
                    placeholder="Number of spots available (leave blank for unlimited)"
                />
            </Form.Group>
        </>
    );
}

StepScheduleCommitment.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
};

/**
 * Step 4: Application & Contact
 */
export function StepApplicationContact({
    formData,
    handleChange,
    validationErrors,
}) {
    return (
        <>
            <h4 className="text-primary mb-4">Application & Contact</h4>

            <Form.Group className="mb-3">
                <Form.Label>Application Method</Form.Label>
                <div className="d-flex gap-3">
                    {APPLICATION_METHODS.map((method) => (
                        <Form.Check
                            key={method.value}
                            type="radio"
                            id={`method-${method.value}`}
                            name="applicationMethod"
                            label={method.label}
                            value={method.value}
                            checked={
                                formData.applicationMethod === method.value
                            }
                            onChange={handleChange}
                        />
                    ))}
                </div>
            </Form.Group>

            {formData.applicationMethod === 'external_link' && (
                <Form.Group className="mb-3">
                    <Form.Label>Application URL *</Form.Label>
                    <Form.Control
                        type="url"
                        name="applicationUrl"
                        value={formData.applicationUrl}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.applicationUrl}
                        placeholder="https://..."
                    />
                    <Form.Control.Feedback type="invalid">
                        {validationErrors.applicationUrl}
                    </Form.Control.Feedback>
                </Form.Group>
            )}

            {formData.applicationMethod === 'email' && (
                <Form.Group className="mb-3">
                    <Form.Label>Application Email *</Form.Label>
                    <Form.Control
                        type="email"
                        name="applicationEmail"
                        value={formData.applicationEmail}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.applicationEmail}
                        placeholder="apply@organization.org"
                    />
                    <Form.Control.Feedback type="invalid">
                        {validationErrors.applicationEmail}
                    </Form.Control.Feedback>
                </Form.Group>
            )}

            <Row>
                <Col md={6}>
                    {!formData.isRolling && (
                        <Form.Group className="mb-3">
                            <Form.Label>Application Deadline</Form.Label>
                            <Form.Control
                                type="date"
                                name="applicationDeadline"
                                value={formData.applicationDeadline}
                                onChange={handleChange}
                                isInvalid={
                                    !!validationErrors.applicationDeadline
                                }
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.applicationDeadline}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}
                </Col>
                <Col md={6} className="d-flex align-items-end">
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="isRolling"
                            name="isRolling"
                            label="Rolling applications (no deadline)"
                            checked={formData.isRolling}
                            onChange={(e) =>
                                handleChange({
                                    target: {
                                        name: 'isRolling',
                                        value: e.target.checked,
                                    },
                                })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Requirements</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="List requirements, one per line"
                />
                <Form.Text className="text-muted">
                    One requirement per line
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Skills Developed</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="skillsDeveloped"
                    value={formData.skillsDeveloped}
                    onChange={handleChange}
                    placeholder="List skills participants will develop, one per line"
                />
                <Form.Text className="text-muted">One skill per line</Form.Text>
            </Form.Group>

            <h5 className="text-primary mt-4 mb-3">Contact Information</h5>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                            placeholder="Full name"
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Contact Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="contactEmail"
                            value={formData.contactEmail}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.contactEmail}
                            placeholder="email@org.com"
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.contactEmail}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Contact Phone</Form.Label>
                        <Form.Control
                            type="tel"
                            name="contactPhone"
                            value={formData.contactPhone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
}

StepApplicationContact.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
};

/**
 * Step 5 (conditional): Event Details
 */
export function StepEventDetails({
    formData,
    handleChange,
    validationErrors,
    orgTimezone,
}) {
    const showVirtualLink =
        formData.locationMode === 'remote' ||
        formData.locationMode === 'hybrid';

    // Build the dropdown options against both the org's timezone (ticket
    // #307: derived from the org, not the ecosystem) and the opportunity's
    // own currently selected value. The two can differ in edit mode: the
    // derive+override design means a stored value can outlive a later
    // change to the org's own timezone. Merging the stored value LAST so
    // it ends up first in the list ensures the <Form.Select> always has a
    // matching option for what's actually selected, not just for the org.
    const timezoneOptions = React.useMemo(
        () =>
            mergeTimezoneOption(
                formData.eventTimezone,
                mergeTimezoneOption(orgTimezone)
            ),
        [orgTimezone, formData.eventTimezone]
    );

    return (
        <>
            <h4 className="text-primary mb-4">Event Details</h4>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Start Time *</Form.Label>
                        <Form.Control
                            type="time"
                            name="eventStartTime"
                            value={formData.eventStartTime}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.eventStartTime}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.eventStartTime}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>End Time</Form.Label>
                        <Form.Control
                            type="time"
                            name="eventEndTime"
                            value={formData.eventEndTime}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.eventEndTime}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.eventEndTime}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Timezone</Form.Label>
                        <Form.Select
                            name="eventTimezone"
                            value={formData.eventTimezone}
                            onChange={handleChange}
                        >
                            {timezoneOptions.map((tz) => (
                                <option key={tz.value} value={tz.value}>
                                    {tz.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    id="eventIsRecurring"
                    name="eventIsRecurring"
                    label="This is a recurring event"
                    checked={formData.eventIsRecurring}
                    onChange={(e) =>
                        handleChange({
                            target: {
                                name: 'eventIsRecurring',
                                value: e.target.checked,
                            },
                        })
                    }
                />
            </Form.Group>

            {formData.eventIsRecurring && (
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Recurrence Pattern</Form.Label>
                            <Form.Select
                                name="recurrencePattern"
                                value={formData.recurrencePattern}
                                onChange={handleChange}
                            >
                                <option value="">Select pattern</option>
                                {RECURRENCE_PATTERNS.map((rp) => (
                                    <option key={rp.value} value={rp.value}>
                                        {rp.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Recurrence End Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="recurrenceEndDate"
                                value={formData.recurrenceEndDate}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}

            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    id="registrationRequired"
                    name="registrationRequired"
                    label="Registration is required"
                    checked={formData.registrationRequired}
                    onChange={(e) =>
                        handleChange({
                            target: {
                                name: 'registrationRequired',
                                value: e.target.checked,
                            },
                        })
                    }
                />
            </Form.Group>

            {formData.registrationRequired && (
                <Form.Group className="mb-3">
                    <Form.Label>Registration URL</Form.Label>
                    <Form.Control
                        type="url"
                        name="registrationUrl"
                        value={formData.registrationUrl}
                        onChange={handleChange}
                        placeholder="https://..."
                    />
                </Form.Group>
            )}

            {showVirtualLink && (
                <Form.Group className="mb-3">
                    <Form.Label>Virtual Meeting Link</Form.Label>
                    <Form.Control
                        type="url"
                        name="virtualLink"
                        value={formData.virtualLink}
                        onChange={handleChange}
                        placeholder="https://zoom.us/..."
                    />
                </Form.Group>
            )}
        </>
    );
}

StepEventDetails.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    orgTimezone: PropTypes.string,
};

/**
 * Step 6 (conditional): Funding Details
 */
export function StepFundingDetails({
    formData,
    handleChange,
    handleMultiSelect,
    validationErrors,
    organizationSectors,
}) {
    // Opportunity partnerships are a per-tenant capability. When the active
    // ecosystem has disabled `opportunityPartnerships`, hide the "seeking
    // collaboration partners" authoring control so authors in that tenant
    // can't set a flag whose downstream surfaces are also hidden.
    const partnershipsEnabled = useCapability('opportunityPartnerships');
    return (
        <>
            <h4 className="text-primary mb-4">Funding Details</h4>

            <Form.Group className="mb-3">
                <Form.Label>Funding Type *</Form.Label>
                <Form.Select
                    name="fundingType"
                    value={formData.fundingType}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.fundingType}
                >
                    <option value="">Select funding type</option>
                    {FUNDING_TYPE_OPTIONS.map((ft) => (
                        <option key={ft.value} value={ft.value}>
                            {ft.label}
                        </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                    {validationErrors.fundingType}
                </Form.Control.Feedback>
            </Form.Group>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Minimum Amount ($)</Form.Label>
                        <Form.Control
                            type="number"
                            name="fundingAmountMin"
                            value={formData.fundingAmountMin}
                            onChange={handleChange}
                            min={0}
                            placeholder="Min"
                            isInvalid={!!validationErrors.fundingAmountMin}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.fundingAmountMin}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Maximum Amount ($)</Form.Label>
                        <Form.Control
                            type="number"
                            name="fundingAmountMax"
                            value={formData.fundingAmountMax}
                            onChange={handleChange}
                            min={0}
                            placeholder="Max"
                        />
                    </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="fundingIsEstimate"
                            name="fundingIsEstimate"
                            label="Amount is estimated"
                            checked={formData.fundingIsEstimate}
                            onChange={(e) =>
                                handleChange({
                                    target: {
                                        name: 'fundingIsEstimate',
                                        value: e.target.checked,
                                    },
                                })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Funder</Form.Label>
                <Form.Control
                    type="text"
                    name="fundingFunder"
                    value={formData.fundingFunder}
                    onChange={handleChange}
                    placeholder="Name of the funding organization"
                />
            </Form.Group>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Award Cycle</Form.Label>
                        <Form.Select
                            name="fundingAwardCycle"
                            value={formData.fundingAwardCycle}
                            onChange={handleChange}
                        >
                            <option value="">Select cycle</option>
                            {AWARD_CYCLES.map((ac) => (
                                <option key={ac.value} value={ac.value}>
                                    {ac.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Number of Awards</Form.Label>
                        <Form.Control
                            type="number"
                            name="fundingNumberOfAwards"
                            value={formData.fundingNumberOfAwards}
                            onChange={handleChange}
                            min={0}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Funding Duration</Form.Label>
                        <Form.Control
                            type="text"
                            name="fundingDuration"
                            value={formData.fundingDuration}
                            onChange={handleChange}
                            placeholder="e.g., 12 months"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Eligible Organization Types</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                    What types of organizations are eligible?
                </Form.Text>
                <div className="d-flex flex-wrap gap-2">
                    {organizationSectors.map((sector) => (
                        <Form.Check
                            key={sector}
                            type="checkbox"
                            id={`eligible-${sector}`}
                            label={sector}
                            checked={(
                                formData.fundingEligibleOrgTypes || []
                            ).includes(sector)}
                            onChange={() =>
                                handleMultiSelect(
                                    'fundingEligibleOrgTypes',
                                    sector
                                )
                            }
                        />
                    ))}
                </div>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Eligible Activities</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="fundingEligibleActivities"
                    value={formData.fundingEligibleActivities}
                    onChange={handleChange}
                    placeholder="What can the funding be used for? (one per line)"
                />
                <Form.Text className="text-muted">
                    One activity per line
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Check
                    type="checkbox"
                    id="fundingMatchRequired"
                    name="fundingMatchRequired"
                    label="Match funding required"
                    checked={formData.fundingMatchRequired}
                    onChange={(e) =>
                        handleChange({
                            target: {
                                name: 'fundingMatchRequired',
                                value: e.target.checked,
                            },
                        })
                    }
                />
            </Form.Group>

            {formData.fundingMatchRequired && (
                <Form.Group className="mb-3">
                    <Form.Label>Match Details</Form.Label>
                    <Form.Control
                        type="text"
                        name="fundingMatchDetails"
                        value={formData.fundingMatchDetails}
                        onChange={handleChange}
                        placeholder="e.g., 1:1 match required"
                    />
                </Form.Group>
            )}

            {partnershipsEnabled && (
                <Form.Group className="mb-3">
                    <Form.Check
                        type="checkbox"
                        id="fundingSeekingPartners"
                        name="fundingSeekingPartners"
                        label="Seeking collaboration partners for this funding"
                        checked={formData.fundingSeekingPartners}
                        onChange={(e) =>
                            handleChange({
                                target: {
                                    name: 'fundingSeekingPartners',
                                    value: e.target.checked,
                                },
                            })
                        }
                    />
                </Form.Group>
            )}

            {partnershipsEnabled && formData.fundingSeekingPartners && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Partner Requirements</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="fundingPartnerRequirements"
                            value={formData.fundingPartnerRequirements}
                            onChange={handleChange}
                            placeholder="What are you looking for in a partner?"
                        />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner Contact Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="fundingPartnerContactName"
                                    value={formData.fundingPartnerContactName}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner Contact Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="fundingPartnerContactEmail"
                                    value={formData.fundingPartnerContactEmail}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

StepFundingDetails.propTypes = {
    formData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleMultiSelect: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    organizationSectors: PropTypes.array.isRequired,
};

/**
 * Review Step: read-only summary of all entered data
 */
export function StepReview({ formData, goToStep, steps }) {
    // Find step numbers dynamically
    const findStepNum = (title) => {
        const s = steps.find((st) => st.title === title);
        return s ? s.num : 1;
    };

    return (
        <>
            <h4 className="text-primary mb-4">Review Your Opportunity</h4>
            <p className="text-muted mb-4">
                Review the information below. Click &quot;Edit&quot; on any
                section to make changes.
            </p>

            {/* Basics Review */}
            <Card className="mb-3 border">
                <Card.Header
                    className="bg-light d-flex justify-content-between align-items-center review-step-header"
                    role="button"
                    onClick={() => goToStep(findStepNum('Basics'))}
                >
                    <strong>Basic Information</strong>
                    <small className="text-primary">Edit</small>
                </Card.Header>
                <Card.Body>
                    <p className="mb-2">
                        <strong>Title:</strong> {formData.title}
                    </p>
                    <p className="mb-2">
                        <strong>Type:</strong>{' '}
                        <Badge bg="primary">{formData.type}</Badge>
                    </p>
                    <p className="mb-2">
                        <strong>Description:</strong>
                        <br />
                        {formData.description}
                    </p>
                    {formData.focusAreas && (
                        <p className="mb-2">
                            <strong>Focus Areas:</strong> {formData.focusAreas}
                        </p>
                    )}
                    {formData.tags && (
                        <p className="mb-0">
                            <strong>Tags:</strong> {formData.tags}
                        </p>
                    )}
                </Card.Body>
            </Card>

            {/* Audience & Location Review */}
            <Card className="mb-3 border">
                <Card.Header
                    className="bg-light d-flex justify-content-between align-items-center review-step-header"
                    role="button"
                    onClick={() => goToStep(findStepNum('Audience & Location'))}
                >
                    <strong>Audience & Location</strong>
                    <small className="text-primary">Edit</small>
                </Card.Header>
                <Card.Body>
                    {formData.targetAudience?.length > 0 && (
                        <p className="mb-2">
                            <strong>Target Audience:</strong>{' '}
                            {formData.targetAudience.map((a) => (
                                <Badge bg="info" key={a} className="me-1 mb-1">
                                    {a}
                                </Badge>
                            ))}
                        </p>
                    )}
                    {formData.regions?.length > 0 && (
                        <p className="mb-2">
                            <strong>Regions:</strong>{' '}
                            {formData.regions.map((r) => (
                                <Badge
                                    bg="secondary"
                                    key={r}
                                    className="me-1 mb-1"
                                >
                                    {r}
                                </Badge>
                            ))}
                        </p>
                    )}
                    <p className="mb-0">
                        <strong>Location:</strong>{' '}
                        {formData.locationMode === 'remote'
                            ? 'Remote'
                            : formData.locationMode === 'hybrid'
                              ? 'Hybrid'
                              : 'In-Person'}
                        {formData.city &&
                            ` - ${formData.city}, ${formData.state}`}
                    </p>
                </Card.Body>
            </Card>

            {/* Schedule & Commitment Review */}
            <Card className="mb-3 border">
                <Card.Header
                    className="bg-light d-flex justify-content-between align-items-center review-step-header"
                    role="button"
                    onClick={() =>
                        goToStep(findStepNum('Schedule & Commitment'))
                    }
                >
                    <strong>Schedule & Commitment</strong>
                    <small className="text-primary">Edit</small>
                </Card.Header>
                <Card.Body>
                    {formData.startDate && (
                        <p className="mb-2">
                            <strong>Start Date:</strong> {formData.startDate}
                        </p>
                    )}
                    {formData.endDate && !formData.isOngoing && (
                        <p className="mb-2">
                            <strong>End Date:</strong> {formData.endDate}
                        </p>
                    )}
                    {formData.isOngoing && (
                        <p className="mb-2">
                            <strong>Duration:</strong> Ongoing
                        </p>
                    )}
                    {formData.commitmentType && (
                        <p className="mb-2">
                            <strong>Commitment:</strong>{' '}
                            {formData.commitmentType}
                            {formData.hoursPerWeek &&
                                ` (${formData.hoursPerWeek} hrs/week)`}
                        </p>
                    )}
                    {formData.isPaid && (
                        <p className="mb-0">
                            <strong>Compensation:</strong> Paid
                            {formData.compensationDetails &&
                                ` - ${formData.compensationDetails}`}
                        </p>
                    )}
                </Card.Body>
            </Card>

            {/* Application & Contact Review */}
            <Card className="mb-3 border">
                <Card.Header
                    className="bg-light d-flex justify-content-between align-items-center review-step-header"
                    role="button"
                    onClick={() =>
                        goToStep(findStepNum('Application & Contact'))
                    }
                >
                    <strong>Application & Contact</strong>
                    <small className="text-primary">Edit</small>
                </Card.Header>
                <Card.Body>
                    {formData.applicationMethod && (
                        <p className="mb-2">
                            <strong>Application:</strong>{' '}
                            {formData.applicationMethod === 'external_link'
                                ? formData.applicationUrl
                                : formData.applicationMethod === 'email'
                                  ? formData.applicationEmail
                                  : 'In platform'}
                        </p>
                    )}
                    {formData.isRolling ? (
                        <p className="mb-2">
                            <Badge bg="info">Rolling Applications</Badge>
                        </p>
                    ) : formData.applicationDeadline ? (
                        <p className="mb-2">
                            <strong>Deadline:</strong>{' '}
                            {formData.applicationDeadline}
                        </p>
                    ) : null}
                    {formData.contactName && (
                        <p className="mb-0">
                            <strong>Contact:</strong> {formData.contactName}
                            {formData.contactEmail &&
                                ` (${formData.contactEmail})`}
                        </p>
                    )}
                </Card.Body>
            </Card>

            {/* Event Details Review (conditional) */}
            {isEventType(formData.type) && (
                <Card className="mb-3 border">
                    <Card.Header
                        className="bg-light d-flex justify-content-between align-items-center review-step-header"
                        role="button"
                        onClick={() => goToStep(findStepNum('Event Details'))}
                    >
                        <strong>Event Details</strong>
                        <small className="text-primary">Edit</small>
                    </Card.Header>
                    <Card.Body>
                        {formData.eventStartTime && (
                            <p className="mb-2">
                                <strong>Time:</strong> {formData.eventStartTime}
                                {formData.eventEndTime &&
                                    ` - ${formData.eventEndTime}`}
                            </p>
                        )}
                        {formData.eventIsRecurring && (
                            <p className="mb-2">
                                <strong>Recurring:</strong>{' '}
                                {formData.recurrencePattern}
                            </p>
                        )}
                        {formData.registrationRequired && (
                            <p className="mb-0">
                                <strong>Registration:</strong> Required
                                {formData.registrationUrl &&
                                    ` (${formData.registrationUrl})`}
                            </p>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Funding Details Review (conditional) */}
            {isFundingType(formData.type) && (
                <Card className="mb-3 border">
                    <Card.Header
                        className="bg-light d-flex justify-content-between align-items-center review-step-header"
                        role="button"
                        onClick={() => goToStep(findStepNum('Funding Details'))}
                    >
                        <strong>Funding Details</strong>
                        <small className="text-primary">Edit</small>
                    </Card.Header>
                    <Card.Body>
                        {formData.fundingType && (
                            <p className="mb-2">
                                <strong>Type:</strong> {formData.fundingType}
                            </p>
                        )}
                        {(formData.fundingAmountMin ||
                            formData.fundingAmountMax) && (
                            <p className="mb-2">
                                <strong>Amount:</strong>{' '}
                                {formData.fundingAmountMin &&
                                    `$${Number(formData.fundingAmountMin).toLocaleString()}`}
                                {formData.fundingAmountMin &&
                                    formData.fundingAmountMax &&
                                    ' - '}
                                {formData.fundingAmountMax &&
                                    `$${Number(formData.fundingAmountMax).toLocaleString()}`}
                                {formData.fundingIsEstimate && ' (estimated)'}
                            </p>
                        )}
                        {formData.fundingSeekingPartners && (
                            <p className="mb-0">
                                <Badge bg="warning" text="dark">
                                    Seeking Partners
                                </Badge>
                            </p>
                        )}
                    </Card.Body>
                </Card>
            )}
        </>
    );
}

StepReview.propTypes = {
    formData: PropTypes.object.isRequired,
    goToStep: PropTypes.func.isRequired,
    steps: PropTypes.array.isRequired,
};
